import { breadcrumbJsonLd, defineBrowserModule } from "@amtana/browser";
import type { BrowserModule, SeoDeclaration } from "@amtana/browser";

import { withBlogScreens } from "./blog.web.ts";
import { machineLinks } from "./model/machine-links.ts";
import { WARIKE_SITE, WARIKE_WORDS } from "./model/warike-words.ts";

// The declaration: the module's one door for its screens, and for the files its build writes. Each screen carries `seo` next to its
// `title` (`screen-seo` holds this app too, ADR-035). Every screen exists in English alone, so an
// address under "/es" finds none of them and answers not-found.

const LANGUAGES = ["en"] as const;
const WORDS_FILE = "modules/warike-site/browser/src/model/warike-words.ts";
const LEGAL_FILE = "modules/warike-site/browser/content/legal/legal-documents.json";
const HOME_CRUMB = { name: "Home", path: "/" };

const homeSeo: SeoDeclaration = {
  description: WARIKE_WORDS.home.description,
  contentType: "landing",
  alternateLinks: () => machineLinks(),
  sources: ["modules/warike-site/browser/src/ui/blocks/warike-landing.tsx", WORDS_FILE],
  /** Who the site is about: the same two blocks warike.tech carries today. */
  jsonLd: () => [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: WARIKE_WORDS.brand,
      url: WARIKE_SITE.url,
      description: WARIKE_WORDS.home.description,
      logo: `${WARIKE_SITE.url}${WARIKE_SITE.logo}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: WARIKE_WORDS.brand,
      url: WARIKE_SITE.url,
      description: WARIKE_WORDS.home.description,
    },
  ],
};

const contactSeo: SeoDeclaration = {
  description: WARIKE_WORDS.contact.description,
  contentType: "landing",
  // Word for word what warike.tech serves, which gives it no date. A commit here dates a move or a
  // redesign, never the words, so no date is the true one. Words that change set a day here (D1347).
  lastmod: null,
  alternateLinks: () => machineLinks("/contact"),
  jsonLd: () => [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Warike",
      url: `${WARIKE_SITE.url}/contact`,
      mainEntity: {
        "@type": "Organization",
        name: WARIKE_WORDS.brand,
        url: WARIKE_SITE.url,
        knowsAbout: WARIKE_WORDS.contact.topics.map((topic) => topic.title),
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "sales",
          url: WARIKE_SITE.booking,
          availableLanguage: ["English"],
        },
      },
    },
    breadcrumbJsonLd({
      siteUrl: WARIKE_SITE.url,
      items: [HOME_CRUMB, { name: "Contact", path: "/contact" }],
    }),
  ],
};

/** A legal page's head: its date is the document's, never the day it moved here. */
function legalSeo(slug: keyof typeof WARIKE_WORDS.legal): SeoDeclaration {
  const doc = WARIKE_WORDS.legal[slug];
  const path = `/legal/${slug}`;
  return {
    description: doc.description,
    contentType: "landing",
    lastmod: WARIKE_WORDS.legalUpdatedIso,
    alternateLinks: () => machineLinks(path),
    sources: [LEGAL_FILE],
    jsonLd: () => [
      breadcrumbJsonLd({ siteUrl: WARIKE_SITE.url, items: [HOME_CRUMB, { name: doc.name, path }] }),
    ],
  };
}

const notFoundSeo: SeoDeclaration = {
  description: WARIKE_WORDS.notFound.metaDescription,
  noindex: true,
  sitemap: false,
  alternateLinks: () => machineLinks(),
};

export const warikeSiteBrowserModule: BrowserModule = withBlogScreens(
  defineBrowserModule("warike-site").withScreens({
    home: {
      path: "/",
      languages: LANGUAGES,
      title: WARIKE_WORDS.home.title,
      seo: homeSeo,
      load: () => import("./ui/sections/home-screen.tsx"),
    },
    contact: {
      path: "/contact",
      languages: LANGUAGES,
      title: WARIKE_WORDS.contact.title,
      seo: contactSeo,
      load: () => import("./ui/sections/contact-screen.tsx"),
    },
    brand: {
      path: "/brand",
      languages: LANGUAGES,
      title: WARIKE_WORDS.brandPage.title,
      seo: {
        description: WARIKE_WORDS.brandPage.description,
        contentType: "landing",
        alternateLinks: () => machineLinks(),
        sources: [
          "modules/warike-site/browser/src/ui/sections/brand-screen.tsx",
          "modules/warike-site/browser/src/model/brand-book-words.ts",
          WORDS_FILE,
        ],
      },
      load: () => import("./ui/sections/brand-screen.tsx"),
    },
    legalPrivacy: {
      path: "/legal/privacy-policy",
      languages: LANGUAGES,
      title: WARIKE_WORDS.legalTitle(WARIKE_WORDS.legal["privacy-policy"].name),
      seo: legalSeo("privacy-policy"),
      load: () => import("./ui/sections/legal-privacy-policy-screen.tsx"),
    },
    legalTerms: {
      path: "/legal/terms-conditions",
      languages: LANGUAGES,
      title: WARIKE_WORDS.legalTitle(WARIKE_WORDS.legal["terms-conditions"].name),
      seo: legalSeo("terms-conditions"),
      load: () => import("./ui/sections/legal-terms-conditions-screen.tsx"),
    },
    legalImpressum: {
      path: "/legal/impressum",
      languages: LANGUAGES,
      title: WARIKE_WORDS.legalTitle(WARIKE_WORDS.legal["impressum"].name),
      seo: legalSeo("impressum"),
      load: () => import("./ui/sections/legal-impressum-screen.tsx"),
    },
    notFound: {
      path: "*",
      title: WARIKE_WORDS.notFound.pageTitle,
      seo: notFoundSeo,
      load: () => import("./ui/sections/not-found-screen.tsx"),
    },
  }),
).withSiteFiles(async (built) => (await import("./behavior/machine-files.ts")).machineFiles(built));
